import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { 
  Search, 
  Users, 
  CheckCircle,
  Clock,
  AlertTriangle,
  Table,
  ArrowUpAZ,
  Hash,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicView = () => {
  const { token } = useParams();
  const [event, setEvent] = useState(null);
  const [buchungen, setBuchungen] = useState([]);
  const [tische, setTische] = useState([]);
  const [filteredBuchungen, setFilteredBuchungen] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name' or 'table'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const refreshIntervalRef = React.useRef(null);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  useEffect(() => {
    filterAndSortBuchungen();
  }, [buchungen, searchTerm, sortBy]);

  // Auto-refresh for real-time sync
  useEffect(() => {
    if (autoRefresh && token) {
      refreshIntervalRef.current = setInterval(() => {
        fetchData();
      }, 5000); // Refresh every 5 seconds
      
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [autoRefresh, token]);

  const fetchData = async () => {
    try {
      // Use axios without authentication
      const response = await axios.get(`${API}/public/events/${token}`);
      
      setEvent(response.data.event);
      setBuchungen(response.data.buchungen || []);
      setTische(response.data.tische || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching public event data:', err);
      setError('Fehler beim Laden der Event-Daten. Link ungültig oder Event nicht gefunden.');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortBuchungen = () => {
    let filtered = [...buchungen];
    
    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(buchung => 
        (buchung.vorname && buchung.vorname.toLowerCase().includes(search)) ||
        (buchung.nachname && buchung.nachname.toLowerCase().includes(search)) ||
        (buchung.firma && buchung.firma.toLowerCase().includes(search))
      );
    }
    
    // Sort
    if (sortBy === 'name') {
      filtered.sort((a, b) => {
        const nameA = a.firma ? a.firma.toLowerCase() : (a.nachname || '').toLowerCase();
        const nameB = b.firma ? b.firma.toLowerCase() : (b.nachname || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } else if (sortBy === 'table') {
      filtered.sort((a, b) => {
        const tischA = getTischByBuchung(a);
        const tischB = getTischByBuchung(b);
        
        // Put unassigned tables at the end
        if (!tischA && !tischB) return 0;
        if (!tischA) return 1;
        if (!tischB) return -1;
        
        // Sort by table number (numerically first, then alphabetically)
        const numA = parseInt(tischA.tischnummer) || 0;
        const numB = parseInt(tischB.tischnummer) || 0;
        if (numA !== numB) return numA - numB;
        return tischA.tischnummer.localeCompare(tischB.tischnummer);
      });
    }
    
    setFilteredBuchungen(filtered);
  };

  const getTischByBuchung = (buchung) => {
    if (!buchung.tisch_id || !tische || tische.length === 0) {
      return null;
    }
    return tische.find(t => t.id === buchung.tisch_id);
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'gar_nicht_da':
        return (
          <Badge className="bg-gray-100 text-gray-700 flex items-center">
            <Clock className="w-3 h-3 mr-1" />
            Nicht da
          </Badge>
        );
      case 'teilweise_da':
        return (
          <Badge className="bg-amber-100 text-amber-700 flex items-center">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Teilweise
          </Badge>
        );
      case 'vollstaendig_da':
        return (
          <Badge className="bg-green-100 text-green-700 flex items-center">
            <CheckCircle className="w-3 h-3 mr-1" />
            Vollstaendig da
          </Badge>
        );
      default:
        return <Badge className="bg-gray-100 text-gray-700">Unbekannt</Badge>;
    }
  };

  const getDisplayName = (buchung) => {
    if (buchung.firma) {
      return buchung.firma;
    }
    if (buchung.nachname && buchung.vorname) {
      return `${buchung.nachname}, ${buchung.vorname}`;
    }
    return buchung.nachname || buchung.vorname || 'Unbekannt';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Event-Daten...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-8">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Zugriff nicht möglich</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {event?.name || 'Event'}
              </h1>
              {event?.datum && (
                <p className="text-sm text-gray-600">
                  {format(new Date(event.datum), 'dd.MM.yyyy HH:mm', { locale: de })}
                </p>
              )}
            </div>
            
            {/* Auto-Refresh Indicator */}
            <div 
              className="flex items-center space-x-2 px-3 py-1 bg-green-50 border border-green-200 rounded-md cursor-pointer hover:bg-green-100 transition-all"
              onClick={() => setAutoRefresh(!autoRefresh)}
              title={autoRefresh ? "Auto-Sync aktiv (alle 5 Sek.)" : "Auto-Sync deaktiviert"}
            >
              <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'text-green-600 animate-spin' : 'text-gray-400'}`} />
              <span className={`text-xs font-medium ${autoRefresh ? 'text-green-700' : 'text-gray-600'}`}>
                {autoRefresh ? 'Live-Sync' : 'Sync Aus'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Search and Sort Controls */}
        <div className="mb-6 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Nach Name oder Firma suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 text-base py-2 bg-white shadow-sm border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>
          
          {/* Sort Buttons */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Sortieren nach:</span>
            <Button
              variant={sortBy === 'name' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSortBy('name')}
              className={sortBy === 'name' ? 'bg-blue-500 hover:bg-blue-600 text-white' : ''}
            >
              <ArrowUpAZ className="w-4 h-4 mr-1" />
              Name
            </Button>
            <Button
              variant={sortBy === 'table' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSortBy('table')}
              className={sortBy === 'table' ? 'bg-blue-500 hover:bg-blue-600 text-white' : ''}
            >
              <Hash className="w-4 h-4 mr-1" />
              Tischnummer
            </Button>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <Users className="w-4 h-4 mr-2 text-blue-600" />
                Buchungen gesamt
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">{buchungen.length}</div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                Vollständig eingecheckt
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {buchungen.filter(b => b.status === 'vollstaendig_da').length}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <Clock className="w-4 h-4 mr-2 text-gray-600" />
                Noch nicht da
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {buchungen.filter(b => b.status === 'gar_nicht_da').length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bookings List */}
        {filteredBuchungen.length === 0 ? (
          <Card className="bg-white shadow-sm text-center py-8">
            <CardContent>
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchTerm ? 'Keine Treffer gefunden' : 'Keine Buchungen vorhanden'}
              </h3>
              <p className="text-gray-600">
                {searchTerm 
                  ? `Keine Buchungen entsprechen der Suche "${searchTerm}"`
                  : 'Es sind noch keine Buchungen für dieses Event vorhanden.'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredBuchungen.map((buchung, index) => {
              const tisch = getTischByBuchung(buchung);
              
              return (
                <Card 
                  key={buchung.id}
                  className="bg-white shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        {/* Guest Name/Company */}
                        {buchung.firma ? (
                          <div className="mb-1">
                            <h3 className="text-base font-semibold text-gray-900">
                              {buchung.firma}
                            </h3>
                            {buchung.vorname && buchung.nachname && (
                              <p className="text-xs text-gray-600">
                                {buchung.nachname}, {buchung.vorname}
                              </p>
                            )}
                          </div>
                        ) : (
                          <h3 className="text-base font-semibold text-gray-900 mb-1">
                            {buchung.nachname && buchung.vorname 
                              ? `${buchung.nachname}, ${buchung.vorname}`
                              : buchung.nachname || buchung.vorname || 'Unbekannt'
                            }
                          </h3>
                        )}
                        
                        {/* Info Row */}
                        <div className="flex items-center flex-wrap gap-3">
                          {/* Status Badge */}
                          {getStatusBadge(buchung.status)}
                          
                          {/* Tickets Count */}
                          <span className="text-sm text-gray-700 font-medium">
                            {buchung.tickets_anwesend} / {buchung.tickets_erwartet} Personen
                          </span>
                          
                          {/* Table Number */}
                          {tisch && (
                            <div className="flex items-center space-x-1 text-sm text-gray-600">
                              <Table className="w-4 h-4 text-blue-600" />
                              <span className="font-medium">Tisch {tisch.tischnummer}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicView;
